// src/components/Common/RightClickProtection.jsx
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * RightClickProtection Component
 * Wrapper component that disables right-click and drag-to-save on images
 * Provides basic image protection without affecting other functionality
 *
 * @param {React.ReactNode} children - Child components to protect
 * @param {string} message - Optional custom message to show on right-click attempt
 */
const RightClickProtection = ({ children, message }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Prevent right-click context menu on images
        const handleContextMenu = (e) => {
            // Only prevent on IMG elements
            if (e.target.tagName === 'IMG') {
                e.preventDefault();

                // Show custom message if provided
                if (message) {
                    // Create a temporary toast notification
                    const toast = document.createElement('div');
                    toast.textContent = message;
                    toast.style.cssText = `
                        position: fixed;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        background-color: rgba(0, 0, 0, 0.8);
                        color: white;
                        padding: 12px 24px;
                        border-radius: 4px;
                        z-index: 10000;
                        font-size: 14px;
                        pointer-events: none;
                        animation: fadeInOut 2s ease-in-out;
                    `;

                    // Add fadeInOut animation
                    const style = document.createElement('style');
                    style.textContent = `
                        @keyframes fadeInOut {
                            0% { opacity: 0; }
                            20% { opacity: 1; }
                            80% { opacity: 1; }
                            100% { opacity: 0; }
                        }
                    `;
                    document.head.appendChild(style);

                    document.body.appendChild(toast);

                    // Remove toast after animation
                    setTimeout(() => {
                        document.body.removeChild(toast);
                        document.head.removeChild(style);
                    }, 2000);
                }

                return false;
            }
        };

        // Prevent drag-to-save on images
        const handleDragStart = (e) => {
            if (e.target.tagName === 'IMG') {
                e.preventDefault();
                return false;
            }
        };

        // Prevent selection on images (additional protection)
        const handleSelectStart = (e) => {
            if (e.target.tagName === 'IMG') {
                e.preventDefault();
                return false;
            }
        };

        // Add event listeners
        container.addEventListener('contextmenu', handleContextMenu);
        container.addEventListener('dragstart', handleDragStart);
        container.addEventListener('selectstart', handleSelectStart);

        // Cleanup event listeners on unmount
        return () => {
            container.removeEventListener('contextmenu', handleContextMenu);
            container.removeEventListener('dragstart', handleDragStart);
            container.removeEventListener('selectstart', handleSelectStart);
        };
    }, [message]);

    // Render children with protection
    return (
        <div ref={containerRef} style={{ display: 'contents' }}>
            {children}
        </div>
    );
};

RightClickProtection.propTypes = {
    children: PropTypes.node.isRequired,
    message: PropTypes.string
};

RightClickProtection.defaultProps = {
    message: ''
};

export default RightClickProtection;
